import clsx from "clsx";
import {
  ComponentPropsWithoutRef,
  FC,
  ReactNode,
  useState,
  forwardRef,
  ForwardRefExoticComponent,
  RefAttributes,
} from "react";
import "./scroll-bar.css";

interface TableComponent
  extends ForwardRefExoticComponent<
    ComponentPropsWithoutRef<"div"> & {
      className?: string;
    } & RefAttributes<HTMLDivElement>
  > {
  Header: FC<ComponentPropsWithoutRef<"thead">>;
  HeaderColumn: FC<ComponentPropsWithoutRef<"th"> & { className?: string }>;
  Body: FC<ComponentPropsWithoutRef<"tbody">>;
  Row: FC<
    ComponentPropsWithoutRef<"tr"> & {
      expandedSection?: ReactNode;
      className?: string;
    }
  >;
  Column: FC<ComponentPropsWithoutRef<"td">>;
}

const TableBase = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<"div"> & { className?: string }
>(({ children, className = "", ...rest }, ref) => {
  return (
    <div
      ref={ref}
      className={clsx("custom-scrollbar overflow-y-auto", className)}
      {...rest}
    >
      <table className="w-full table-fixed">{children}</table>
    </div>
  );
});

TableBase.displayName = "Table";

const Header: TableComponent["Header"] = ({ children, ...rest }) => (
  <thead className="dark:bg-black dark:text-white" {...rest}>
    <tr>{children}</tr>
  </thead>
);

const HeaderColumn: TableComponent["HeaderColumn"] = ({
  children,
  className = "text-left",
  ...rest
}) => (
  <th
    className={clsx("font-semibold text-xs min-w-[5rem] pl-4", className)}
    {...rest}
  >
    {children}
  </th>
);

const Body: TableComponent["Body"] = ({ children, ...rest }) => (
  <tbody {...rest}>{children}</tbody>
);

const Row: TableComponent["Row"] = ({
  children,
  expandedSection = null,
  className = "text-left",
  ...rest
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  return (
    <>
      <tr
        className={clsx(
          "cursor-pointer border-b border-[#121521]",
          isExpanded && "bg-gradient-to-r",
          className
        )}
        onClick={() => setIsExpanded((prev) => !prev)}
        {...rest}
      >
        {children}
      </tr>
      {isExpanded && expandedSection}
    </>
  );
};

const Column: TableComponent["Column"] = ({ children, ...rest }) => (
  <td className="pl-4 overflow-hidden" {...rest}>
    {children}
  </td>
);

const Table = TableBase as TableComponent;

Table.Row = Row;
Table.Column = Column;
Table.Header = Header;
Table.HeaderColumn = HeaderColumn;
Table.Body = Body;

export default Table;
